import { useMemo } from 'react';
import { dayjs } from '../../support/helpers/dayjs.ts';
import { IonAccordion, IonAccordionGroup, IonCard, IonCardContent, IonItem, IonLabel, IonList } from '@ionic/react';
import { useSubscription } from '@autopylot-internal/state';
import { useInstance } from '../../support/ioc/useInstance';
import { EditableMissionState } from '../../mission/model/EditableMissionState.ts';
import {  MissionsState } from '../model/MissionsState.ts';
import { MissionListItem } from './MissionListItem.tsx';
import { Refresher } from '../../components/refresher/Refresher.tsx';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { NewMissionButton } from './NewMissionButton.tsx';
import { Spinner } from '../../components/spinner/Spinner.tsx';
import { RefreshTimer } from '../../components/refresher/RefreshTimer.tsx';
import { DrawerLabel } from '../../components/drawer/DrawerLabel.tsx';
import { BulkExportMissionsButton } from '../export/BulkExportMissionsButton.tsx';
import { ButtonBar } from '../../components/button/ButtonBar.tsx';

// TODO: split out
const splitMissions = (missions: EditableMissionState[]) => {
    const needsAttention = [];
    const invalid = [];
    const past = [];
    const future = [];
    const canceled = [];

    for (const mission of missions) {
        const status = mission.getMissionStatus();
        if (mission.shouldCancel() || mission.getReminderCount() > 0) {
            needsAttention.push(mission);
        } else if (mission.isNotValid()) {
            invalid.push(mission);
        } else if (status === 'user-canceled') {
            canceled.push(mission);
        } else if (mission.hasEnded()) {
            past.push(mission);
        } else {
            future.push(mission);
        }
    }
    return { needsAttention, past, future, canceled, invalid };
};

const missionSorts = {
    futureIsLast: (a: EditableMissionState, b: EditableMissionState) => {

        const aEpoch = dayjs(a.getStartTime()).valueOf();
        const bEpoch = dayjs(b.getStartTime()).valueOf();

        return aEpoch - bEpoch;
    },
    pastIsLast: (a: EditableMissionState, b: EditableMissionState) => {

        const aEpoch = dayjs(a.getStartTime()).valueOf();
        const bEpoch = dayjs(b.getStartTime()).valueOf();

        return bEpoch - aEpoch;
    },
};

const LoadingSpinner: React.FC<{missions: MissionsState}> = ({ missions }) => {
    if (missions.state.isFullyLoaded) {
        return null;
    }
    return (
        <div style={{textAlign: 'center', marginBottom: 15}}>
            <Spinner size="small" />
        </div>
    );
};

const SectionLabel: React.FC<{text: string}> = ({ text }) => (
    <div style={{margin: '15px 20px -8px'}}>
        <DrawerLabel text={text} />
    </div>
);

const MissionListButtons: React.FC<{hasAnyMissions: boolean}> = ({ hasAnyMissions }) => (
    <div style={{margin: '25px 15px 15px'}}>
        <ButtonBar>
            <NewMissionButton />
            <BulkExportMissionsButton disabled={!hasAnyMissions} />
        </ButtonBar>
    </div>
);

export const MissionList = () => {
    const missions = useInstance(MissionsState);
    useSubscription(() => missions);

    const tracker = useInstance(TrackingService);

    const { needsAttention, past, future, canceled, invalid } = useMemo(
        () => splitMissions(missions.state.missions),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [missions.state.lastLoaded]
    );

    const onSectionSelected = (event: any) => {
        missions.setState({ expandedSection: event.detail.value });

        if (event.detail.value) {
            tracker.track('Missions Section Opened', () => ({ section: event.detail.value }));
        }
    };

    const hasAnyMissions = missions.state.missions.length > 0;

    return (
        <>
            <Refresher onRefresh={missions.loadAll} />

            {future.length > 0 &&
                <RefreshTimer getIntervalMs={future[0].getAutoRefreshIntervalMs} handler={missions.loadRecentAndUpcoming} />
            }

            {(future.length === 0 && needsAttention.length === 0) ? (
                <IonCard><IonCardContent>
                    <h2 style={{marginBottom: 30}}>Looks like you don't have any upcoming missions - let's create one!</h2>
                    <MissionListButtons hasAnyMissions={hasAnyMissions} />
                </IonCardContent></IonCard>
            ) : (
                <MissionListButtons hasAnyMissions={hasAnyMissions} />
            )}

            <div data-type="needs-attention">
                {needsAttention.length > 0 &&
                    <SectionLabel text="Needs Attention" />
                }
                {needsAttention.sort(missionSorts.futureIsLast).map(mission => (
                    <MissionListItem key={mission.state.missionId} mission={mission} />
                ))}
            </div>

            <div data-type="upcoming-missions">
                {future.length > 0 &&
                    <SectionLabel text="Upcoming" />
                }
                {future.sort(missionSorts.futureIsLast).map(mission => (
                    <MissionListItem key={mission.state.missionId} mission={mission} />
                ))}
            </div>

            <IonAccordionGroup style={{marginBottom: 20}} value={missions.state.expandedSection} onIonChange={onSectionSelected}>
                {past.length > 0 && (
                    <IonAccordion value="past">
                        <IonItem slot="header">
                            <IonLabel>Completed</IonLabel>
                        </IonItem>
                        <IonList slot="content">
                            {past.sort(missionSorts.pastIsLast).map(mission => (
                                <MissionListItem key={mission.state.missionId} mission={mission} />
                            ))}
                            <LoadingSpinner missions={missions} />
                        </IonList>
                    </IonAccordion>
                )}
                {canceled.length > 0 && (
                    <IonAccordion value="canceled">
                        <IonItem slot="header">
                            <IonLabel>Canceled</IonLabel>
                        </IonItem>
                        <IonList slot="content">
                            {canceled.sort(missionSorts.pastIsLast).map(mission => (
                                <MissionListItem key={mission.state.missionId} mission={mission} />
                            ))}
                            <LoadingSpinner missions={missions} />
                        </IonList>
                    </IonAccordion>
                )}
                {invalid.length > 0 && (
                    <IonAccordion value="invalid">
                        <IonItem slot="header">
                            <IonLabel>Not Valid</IonLabel>
                        </IonItem>
                        <IonList slot="content">
                            {invalid.sort(missionSorts.pastIsLast).map(mission => (
                                <MissionListItem key={mission.state.missionId} mission={mission} />
                            ))}
                            <LoadingSpinner missions={missions} />
                        </IonList>
                    </IonAccordion>
                )}
            </IonAccordionGroup>
        </>
    );
};
